import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Chip,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Build as ServiceIcon,
  CheckCircleOutline as ConfirmationIcon,
  LocalOffer as PriceIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useBooking } from '../contexts/BookingContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiService } from '../services/api.js';

const ConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { 
    bookingData,
    paymentSession,
    selectedProducts,
    selectedAddons,
    resetBooking,
    setSelectedProducts,
    setSelectedAddons,
    setLoading,
    setError,
    clearError,
    isLoading,
    error 
  } = useBooking();

  const [confirmation, setConfirmation] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const hasVerifiedRef = useRef(false);


  useEffect(() => {
    // Prevent multiple API calls
    if (hasVerifiedRef.current) return;

    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (cancelled === 'true') {
      navigate('/summary');
      return;
    }

    if (success === 'true' && sessionId) {
      hasVerifiedRef.current = true;
      verifyPaymentAndCreateBooking(sessionId);
    } else if (bookingData?.reference) {
      // Direct navigation to confirmation with existing booking
      setConfirmation({
        ...bookingData,
        booking_date: bookingData.booking_date || bookingData.appointment_date,
        booking_time: bookingData.booking_time || bookingData.appointment_time
      });
      setPaymentStatus('completed');
      hasVerifiedRef.current = true;
    } else {
      // No valid session or booking data
      navigate('/');
    }
  }, [searchParams, bookingData, navigate]);

  const fetchFullBookingDetails = async (bookingId) => {
    try {
      const bookingResponse = await apiService.getBooking(bookingId);
      const data = bookingResponse.data;
      
      // The API returns: { booking: {...}, vehicle: {...}, garage: {...}, products: [...], addons: [...] }
      // Map the booking data to the confirmation format expected by the UI
      setConfirmation({
        id: data.booking.id,
        reference: data.booking.id,
        payment_status: data.booking.payment_status,
        total_amount: parseFloat(data.booking.total_amount),
        vehicle_registration: data.vehicle?.registration_number,
        garage: {
          name: data.garage?.name,
          address: data.garage?.address || 
                  `${data.garage?.address_line1 || ''}${data.garage?.address_line2 ? ', ' + data.garage?.address_line2 : ''}${data.garage?.address_line3 ? ', ' + data.garage?.address_line3 : ''}`.trim(),
          phone: data.garage?.phone
        },
        booking_date: data.booking.booking_date,
        booking_time: data.booking.booking_time,
        created_at: data.booking.created_at
      });
      
      // Also set the products and addons from the API response
      setSelectedProducts(data.products || []);
      setSelectedAddons(data.addons || []);
      
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Use minimal confirmation data if full details fail
      setConfirmation({
        id: bookingId,
        reference: bookingId,
        payment_status: 'paid',
        total_amount: 0,
        booking_date: null,
        booking_time: null,
        vehicle_registration: '',
        garage: { name: '', address: '', phone: '' }
      });
    }
  };

  const verifyPaymentAndCreateBooking = async (sessionId, retryCount = 0) => {
    const maxRetries = 5; // Maximum 5 attempts
    const retryDelay = Math.min(3000 * Math.pow(1.5, retryCount), 10000); // Exponential backoff with max 10s delay
    
    // Only set loading on first attempt
    if (retryCount === 0) {
      setLoading(true);
      clearError();
    }

    try {
      // Verify payment status with retry logic
      const paymentResponse = await apiService.getPaymentStatus(sessionId);
      const booking = paymentResponse.data;
      
      // Check payment status
      if (booking.payment_status === 'paid') {
        // Payment successful - fetch full booking details
        await fetchFullBookingDetails(booking.booking_id);
        setPaymentStatus('completed');
        setLoading(false); // Success - stop loading
        
        // Clear booking context as process is complete
        setTimeout(() => {
          resetBooking();
        }, 1000);
        
      } else if (booking.payment_status === 'pending') {
        // Payment still processing - keep checking with retry limit
        if (retryCount < maxRetries) {
          setPaymentStatus('processing');
          
          // Retry after a delay with exponential backoff - don't set loading false
          setTimeout(() => {
            hasVerifiedRef.current = false; // Allow re-verification
            verifyPaymentAndCreateBooking(sessionId, retryCount + 1);
          }, retryDelay);
          return; // Important: return early to avoid setting loading false
        } else {
          // Max retries reached
          setError('Payment verification is taking longer than expected. Please check your booking in "My Bookings" or contact our support team for assistance.');
          setPaymentStatus('failed');
          setLoading(false); // Failed after max retries - stop loading
        }
        
      } else {
        // Payment failed or other status
        setError('Payment could not be processed successfully. Please try again or contact our support team for assistance.');
        setPaymentStatus('failed');
        setLoading(false); // Failed - stop loading
      }

    } catch (error) {
      console.error('Payment verification error:', error);
      
      // Retry on API errors up to maxRetries
      if (retryCount < maxRetries) {
        setTimeout(() => {
          hasVerifiedRef.current = false; // Allow re-verification
          verifyPaymentAndCreateBooking(sessionId, retryCount + 1);
        }, retryDelay);
        return; // Important: return early to avoid setting loading false
      } else {
        // Max retries reached
        setError('Unable to verify your payment status after multiple attempts. Please check your booking in "My Bookings" or contact our support team.');
        setPaymentStatus('failed');
        setLoading(false); // Failed after max retries - stop loading
      }
    }
  };

  const formatDate = (date) => {
    if (!date) {
      return 'Date not available';
    }
    try {
      return new Date(date).toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid Date';
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    
    // If time is already in AM/PM format, return as is
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    
    // Otherwise, convert from 24-hour format
    try {
      return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting time:', error, time);
      return time;
    }
  };

  const formatAmount = (amount) => {
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    return num && !isNaN(num) ? num.toFixed(2) : '0.00';
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share && confirmation) {
      try {
        await navigator.share({
          title: 'MOT Booking Confirmation',
          text: `MOT booking confirmed for ${confirmation.vehicle_registration} on ${formatDate(confirmation.appointment_date)}`,
          url: window.location.href,
        });
      } catch (error) {
        // Fallback to copy to clipboard
        navigator.clipboard.writeText(window.location.href);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (paymentStatus === 'failed' || error) {
    const handleRetryPayment = async () => {
      if (bookingData && paymentSession) {
        try {
          // Create a new payment session for retry
          const response = await apiService.createPaymentSession(bookingData);
          if (response.data.session_url) {
            // Redirect to new Stripe checkout session
            window.location.href = response.data.session_url;
          }
        } catch (error) {
          console.error('Retry payment error:', error);
          setError('Failed to initiate retry payment. Please contact support.');
        }
      } else {
        // If no booking data, redirect to start over
        navigate('/');
      }
    };

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Booking Unsuccessful
            </Typography>
            <Typography variant="body2">
              {error || 'We were unable to complete your booking at this time. Please try again or contact our customer support team for assistance.'}
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => navigate('/')}>
              Start Over
            </Button>
            {bookingData && (
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleRetryPayment}
                sx={{ 
                  minWidth: 120,
                  '&.Mui-disabled': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    opacity: 0.8
                  }
                }}
              >
                Retry Payment
              </Button>
            )}
            <Button variant="outlined" href="mailto:support@motbooking.com">
              Contact Support
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (isLoading || paymentStatus === 'processing' || (!confirmation && paymentStatus !== 'failed')) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {paymentStatus === 'processing' ? 'Verifying Payment...' : 'Processing your booking...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {paymentStatus === 'processing' 
                ? 'Please wait while we verify your payment status'
                : 'Please wait while we confirm your payment and verify your booking'
              }
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!confirmation) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Success Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <ConfirmationIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2, color: 'success.main' }}>
          Booking Confirmed
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Thank you for your booking. Your MOT test appointment has been successfully confirmed and payment has been processed.
        </Typography>
      </Box>

      {/* Email Confirmation Alert */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon />
          <Typography variant="body2">
            A confirmation email has been sent to <strong>{user?.email || confirmation.customer_email}</strong>
          </Typography>
        </Box>
      </Alert>

      {/* Booking Details */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 'medium', mb: 4 }}>
            {t('confirmation.details')}
          </Typography>

          {/* Booking Reference */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <ReceiptIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                Booking Reference
              </Typography>
            </Box>
            <Box sx={{ ml: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                {confirmation.reference || confirmation.id}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Vehicle */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CarIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                Vehicle
              </Typography>
            </Box>
            <Box sx={{ ml: 4 }}>
              <Typography variant="h6" fontWeight="bold">
                {confirmation.vehicle_registration}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Garage */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <LocationIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                Garage
              </Typography>
            </Box>
            <Box sx={{ ml: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {confirmation.garage?.name || 'Garage'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {confirmation.garage?.address || confirmation.garage_address}
              </Typography>
              {confirmation.garage?.phone && (
                <Typography variant="body1" color="text.secondary">
                  Tel: {confirmation.garage.phone}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Appointment */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <ScheduleIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                Appointment
              </Typography>
            </Box>
            <Box sx={{ ml: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                {confirmation?.booking_date ? formatDate(confirmation.booking_date) : 'Date not available'}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {confirmation?.booking_time ? formatTime(confirmation.booking_time) : 'Time not available'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Services */}
          {(selectedProducts?.length > 0 || selectedAddons?.length > 0) && (
            <>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <ServiceIcon color="primary" sx={{ fontSize: 24 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Selected Services
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  {selectedProducts?.map((product, index) => (
                    <Box key={`product-${index}`} sx={{ mb: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        £{product.default_price || product.price || '0.00'}
                      </Typography>
                    </Box>
                  ))}
                  {selectedAddons?.map((addon, index) => (
                    <Box key={`addon-${index}`} sx={{ mb: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {addon.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(addon.default_price === '0.00' || addon.default_price === 0 || addon.is_free) ? 'Free' : `£${addon.default_price || addon.price || '0.00'}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Divider sx={{ mb: 4 }} />
            </>
          )}

          {/* Total Amount */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <PriceIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                Total
              </Typography>
            </Box>
            <Box sx={{ ml: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                £{formatAmount(confirmation.total_amount)}
              </Typography>
              <Chip 
                label="Paid" 
                color="success" 
                size="small"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Important Information */}
      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
          Important Information
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Please arrive 10 minutes before your appointment time</li>
            <li>Bring your V5C registration document (logbook)</li>
            <li>Ensure your vehicle has adequate fuel for the test</li>
            <li>Remove any personal items from the vehicle</li>
            <li>If you need to reschedule, please contact the test center at least 24 hours in advance</li>
          </ul>
        </Typography>
      </Alert>

      {/* Action Buttons */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ShareIcon />}
            onClick={handleShare}
          >
            Share
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<CalendarIcon />}
            onClick={() => navigate('/bookings')}
          >
            My Bookings
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.8
              }
            }}
          >
            {t('confirmation.backHome')}
          </Button>
        </Grid>
      </Grid>

      {/* Next Steps */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
            What happens next?
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Booking confirmed and payment processed"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <EmailIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Confirmation email sent with all details"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <ScheduleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Reminder email will be sent 24 hours before your appointment"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <LocationIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Arrive at the test center on time with required documents"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ConfirmationPage;